import React, { FC, useEffect, useState } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import Container from '@components/store/container';
import BundleFooterPrice from '@components/templates/bundleDetail/bundleBanner/footer/bundlePrice';
import BundleFooterBannerHighlightImage from '@components/templates/bundleDetail/bundleBanner/footer/highlightImage';
import { BundleFooterBannerContainer } from '@components/templates/bundleDetail/bundleBanner/footer/style';
import BundleBannerPurchaseButton from '@components/templates/bundleDetail/bundleBanner/purchaseButton';
import { GradientBackground } from '@components/templates/bundleDetail/bundleBanner/style';
import useBundleBannerFooter from '@hooks/bundle/useBundleBannerFooter';
import useBundleCourseItem from '@hooks/bundle/useBundleCourseItem';
import { BundleEntity } from '@redux/bundle/type/BundleDetailType';

const HEADER_HIGH_OFFSET = 0;

type BundleFooterBannerProps = {
  headerHeight: number;
  bundleDetailSeo: BundleEntity;
};

const BundleFooterBanner: FC<BundleFooterBannerProps> = ({ headerHeight, bundleDetailSeo }) => {
  const { t } = useTranslation('store_course');
  const { name, price, isEnrolled, bannerPrimaryColor, bannerSecondaryColor } = useBundleBannerFooter();
  const { bundleCourses } = useBundleCourseItem();

  const [scrollY, setScrollY] = useState<number>(0);
  const theme = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const isDisableFooterPrice = isEnrolled;

  return (
    <BundleFooterBannerContainer isSticky={scrollY - HEADER_HIGH_OFFSET > headerHeight}>
      <GradientBackground primaryColor={bannerPrimaryColor} secondaryColor={bannerSecondaryColor}>
        <Container
          containerSx={{
            paddingBlock: {
              md: '30px',
              xs: '16px',
            },
            paddingInline: {
              lg: '0px',
              md: '24px',
            },
            height: '100%',
          }}
          rootSx={{
            height: '100%',
          }}
        >
          <Box display="flex" justifyContent="space-between" height="100%">
            <Box sx={{ display: { md: 'flex', xs: 'none', width: '70%' } }}>
              <Box
                sx={{
                  marginRight: '40px',
                  height: '100px',
                  display: {
                    lg: 'block',
                    md: 'none',
                  },
                }}
              >
                <BundleFooterBannerHighlightImage bundleDetailSeo={bundleDetailSeo} />
              </Box>
              <Box sx={{ paddingRight: '24px' }}>
                <Typography variant="h5" color={theme.palette.textOnPrimary.secondary}>
                  {t('bundle_detail.header_banner.bundle_with_count', { courseCount: bundleCourses.length })}
                </Typography>
                <Typography
                  variant="h4"
                  color={theme.palette.textOnPrimary.primary}
                  sx={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    WebkitLineClamp: 3,
                  }}
                >
                  {name}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: {
                  md: '310px',
                  xs: '100%',
                },
                height: '100%',
              }}
            >
              <Box
                width="100%"
                sx={{
                  display: {
                    md: 'block',
                    xs: 'flex',
                  },
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingInline: {
                    md: '0px',
                    xs: '24px',
                  },
                }}
              >
                {isDisableFooterPrice || !price ? null : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BundleFooterPrice salePrice={price?.salePrice} fullPrice={price?.fullPrice} />
                  </Box>
                )}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <BundleBannerPurchaseButton bundleDetailSeo={bundleDetailSeo} isFooter isDisableActionIcon />
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </GradientBackground>
    </BundleFooterBannerContainer>
  );
};

export default BundleFooterBanner;
